import axios from "axios";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Package,
  Calendar,
  Tag,
  CheckCircle,
  XCircle,
  Star,
  Image as ImageIcon,
  Info,
  Grid,
  Eye,
  Edit2,
  ChevronRight,
  Share2,
  Download,
  AlertCircle,
  Loader2,
  ShoppingBag,
  Layers,
  FileText,
  Shield,
  Truck,
  Clock,
  Users,
  Globe,
  MapPin,
} from "lucide-react";

const ProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/product/${id}`);
        setProduct(res.data.product || res.data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load product details");
        navigate("/product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  const handleEdit = () => {
    navigate(`/products/${id}/edit`);
  };

  const handleBack = () => {
    navigate("/product");
  };

  const handleExport = () => {
    toast.success("Export feature coming soon!");
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.heading,
        text: product.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Product Not Found
          </h3>
          <p className="text-gray-600 mb-6">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate("/product")}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const allImages = [product.mainImage, ...(product.smallImages || [])].filter(
    Boolean
  );
  const hasAdditionalPoints =
    product.additionalPoints && product.additionalPoints.length > 0;
  const hasMoreInfo =
    product.moreProductInfo && product.moreProductInfo.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between py-6">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <button
                onClick={handleBack}
                className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-2 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>

              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Product Details
                  </h1>
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                      product.isActive
                        ? "bg-gradient-to-r from-green-100 to-emerald-100 text-green-700"
                        : "bg-gradient-to-r from-red-100 to-pink-100 text-red-700"
                    }`}
                  >
                    {product.isActive ? (
                      <>
                        <CheckCircle size={12} />
                        Active
                      </>
                    ) : (
                      <>
                        <XCircle size={12} />
                        Inactive
                      </>
                    )}
                  </span>
                </div>
                <p className="text-gray-600 mt-1">
                  View all details and information about this product
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Share2 size={18} />
                Share
              </button>

              <button
                onClick={handleExport}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Download size={18} />
                Export
              </button>
              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Edit2 size={18} />
                Edit Product
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images & Basic Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Product Images */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ImageIcon size={22} />
                  Product Images
                </h2>
              </div>

              <div className="p-6">
                {/* Main Image */}
                <div className="mb-6">
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden">
                    <img
                      src={allImages[activeImage]}
                      alt={product.heading}
                      className="w-full h-[400px] object-cover"
                    />

                    {!product.isActive && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center p-6">
                          <XCircle className="w-16 h-16 text-white/80 mx-auto mb-4" />
                          <p className="text-white text-xl font-semibold">
                            Product Inactive
                          </p>
                          <p className="text-white/80 mt-2">
                            This product is currently not visible to customers
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                {allImages.length > 1 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Gallery
                    </h3>
                    <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                      {allImages.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setActiveImage(index)}
                          className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                            activeImage === index
                              ? "border-indigo-500 ring-2 ring-indigo-200"
                              : "border-transparent hover:border-gray-300"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`View ${index + 1}`}
                            className="w-full h-24 object-cover"
                          />
                          {activeImage === index && (
                            <div className="absolute inset-0 bg-indigo-500/20 flex items-center justify-center">
                              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                                <Eye size={16} className="text-white" />
                              </div>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Details Tabs */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="border-b border-gray-100">
                <div className="flex overflow-x-auto">
                  {[
                    {
                      id: "overview",
                      label: "Overview",
                      icon: <Info size={18} />,
                    },
                    {
                      id: "features",
                      label: "Features",
                      icon: <Grid size={18} />,
                    },
                    {
                      id: "details",
                      label: "Details",
                      icon: <FileText size={18} />,
                    },
                    {
                      id: "additional",
                      label: "Additional Info",
                      icon: <Layers size={18} />,
                    },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? "border-indigo-500 text-indigo-600 bg-indigo-50/50"
                          : "border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6">
                {/* Overview Tab */}
                {activeTab === "overview" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {product.heading}
                      </h3>

                      <div
                        className="text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: product.description,
                        }}
                      />
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <Calendar className="text-indigo-600" size={20} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Created</p>
                            <p className="font-semibold text-gray-900">
                              {new Date(product.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <Clock className="text-green-600" size={20} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">
                              Last Updated
                            </p>
                            <p className="font-semibold text-gray-900">
                              {new Date(product.updatedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Tag className="text-purple-600" size={20} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Category</p>
                            <p className="font-semibold text-gray-900">
                              {product.category?.name || "Uncategorized"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Shield className="text-blue-600" size={20} />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Status</p>
                            <p
                              className={`font-semibold ${
                                product.isActive
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {product.isActive ? "Active" : "Inactive"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Features Tab */}
                {activeTab === "features" && (
                  <div className="space-y-6">
                    {product.bulletDescription &&
                    product.bulletDescription.length > 0 ? (
                      <div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                          Key Features
                        </h3>
                        <ul className="space-y-3">
                          {product.bulletDescription.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-1">
                                <CheckCircle
                                  size={14}
                                  className="text-green-600"
                                />
                              </div>
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Grid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">No features added yet</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Details Tab */}
                {activeTab === "details" && (
                  <div className="space-y-6">
                    {hasAdditionalPoints ? (
                      <div className="space-y-6">
                        <h3 className="text-xl font-semibold text-gray-900">
                          Additional Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {product.additionalPoints.map((point, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 rounded-xl p-5"
                            >
                              <div className="flex items-start gap-4">
                                {point.icon && (
                                  <img
                                    src={point.icon}
                                    alt={point.title}
                                    className="w-12 h-12 rounded-lg object-cover"
                                  />
                                )}
                                <div className="flex-1">
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    {point.title}
                                  </h4>
                                  <p className="text-gray-600 text-sm">
                                    {point.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">
                          No additional details available
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Additional Info Tab */}
                {activeTab === "additional" && (
                  <div className="space-y-8">
                    {hasMoreInfo ? (
                      product.moreProductInfo.map((info, index) => (
                        <div key={index} className="space-y-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <span className="text-indigo-600 font-bold">
                                {index + 1}
                              </span>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {info.title}
                            </h3>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div
                              className="text-gray-700 leading-relaxed"
                              dangerouslySetInnerHTML={{
                                __html: info.description,
                              }}
                            />

                            {info.image && (
                              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden">
                                <img
                                  src={info.image}
                                  alt={info.title}
                                  className="w-full h-48 object-cover"
                                />
                              </div>
                            )}
                          </div>

                          {index < product.moreProductInfo.length - 1 && (
                            <hr className="border-gray-200 my-6" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">
                          No additional information available
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Summary & Actions */}
          <div className="space-y-6">
            {/* Product Summary */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 ">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Product Summary
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Product ID
                  </h3>
                  <p className="text-sm font-mono bg-gray-100 px-3 py-2 rounded-lg truncate">
                    {id}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Status
                    </h3>
                    <p
                      className={`inline-flex items-center gap-1 font-semibold ${
                        product.isActive ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {product.isActive ? (
                        <CheckCircle size={16} />
                      ) : (
                        <XCircle size={16} />
                      )}
                      {product.isActive ? "Active" : "Inactive"}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">
                      Images
                    </h3>
                    <p className="font-semibold text-gray-900">
                      {allImages.length}
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">
                    Category
                  </h3>
                  <p className="font-semibold text-gray-900">
                    {product.category?.name || "Uncategorized"}
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Content Summary
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Key Features</span>
                      <span className="font-semibold">
                        {product.bulletDescription?.length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Additional Points</span>
                      <span className="font-semibold">
                        {product.additionalPoints?.length || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Info Sections</span>
                      <span className="font-semibold">
                        {product.moreProductInfo?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">
                    Metadata
                  </h3>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Created</span>
                      <span className="font-medium">
                        {new Date(product.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Last Updated</span>
                      <span className="font-medium">
                        {new Date(product.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                Quick Actions
              </h2>

              <div className="space-y-3">
                <button
                  onClick={handleEdit}
                  className="w-full flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                      <Edit2 className="text-indigo-600" size={20} />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">
                        Edit Product
                      </p>
                      <p className="text-sm text-gray-600">
                        Update product details
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="text-gray-400" size={20} />
                </button>

                <button
                  onClick={handleBack}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                      <ArrowLeft className="text-gray-600" size={20} />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">
                        Back to List
                      </p>
                      <p className="text-sm text-gray-600">View all products</p>
                    </div>
                  </div>
                  <ChevronRight className="text-gray-400" size={20} />
                </button>

                <button
                  onClick={handleShare}
                  className="w-full flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <Share2 className="text-green-600" size={20} />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">
                        Share Product
                      </p>
                      <p className="text-sm text-gray-600">
                        Share with team members
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="text-gray-400" size={20} />
                </button>
              </div>
            </div>

            {/* Status Alert */}
            {!product.isActive && (
              <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="text-red-500 mt-0.5 flex-shrink-0"
                    size={20}
                  />
                  <div>
                    <h3 className="font-semibold text-red-700 mb-1">
                      Product Inactive
                    </h3>
                    <p className="text-sm text-red-600">
                      This product is currently hidden from customers. Update
                      the status to make it visible.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Category Info */}
            {product.category && (
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 rounded-2xl p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
                    <Package className="text-indigo-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-indigo-900 mb-1">
                      Category Information
                    </h3>
                    <p className="text-sm text-indigo-700">
                      This product belongs to{" "}
                      <strong>{product.category.name}</strong> category.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductView;
